import {Component, Input, OnInit} from '@angular/core';
import {NgbActiveModal} from '@ng-bootstrap/ng-bootstrap';
import {MembershipService} from '../../../../../../@shared/services/membership-service';
import {_AQH_MEM_5} from '../../../../../../@shared/models/input.module';
import {CompanyResponse} from '../../../../../../@shared/models/response.module';
import {ToastService} from '../../../../../../@core/services/toast.service';
import {TranslateService} from '@ngx-translate/core';

@Component({
  selector: 'app-add-membership-plan',
  standalone: false,
  templateUrl: './add-membership-plan.html',
  styleUrl: './add-membership-plan.css'
})
export class AddMembershipPlan implements OnInit {
  @Input() company: CompanyResponse;

  name: string = '';
  description: string = '';
  coverage: string = 'individual';
  billing_cycle: string = 'annual';
  price_cents: number = 0;
  currency: string = 'NOK';
  max_children: number | null = null;
  benefits: string[] = [];
  newBenefit: string = '';
  saving: boolean = false;
  submitted: boolean = false;

  coverageOptions: {value: string, display: string}[] = [];
  billingCycleOptions: {value: string, display: string}[] = [];

  currencyOptions = [
    { code: 'NOK', name: 'NOK' },
    { code: 'SEK', name: 'SEK' },
    { code: 'EUR', name: 'EUR' },
    { code: 'USD', name: 'USD' },
    { code: 'GBP', name: 'GBP' },
    { code: 'DKK', name: 'DKK' },
  ];

  constructor(private membershipService: MembershipService,
              private modal: NgbActiveModal,
              private toastService: ToastService,
              private translate: TranslateService) {
  }

  ngOnInit() {
    this.coverageOptions = [
      {value: 'individual', display: this.translate.instant('companyProfile.membershipPlans.coverage.individual')},
      {value: 'family', display: this.translate.instant('companyProfile.membershipPlans.coverage.family')}
    ];
    this.billingCycleOptions = [
      {value: 'annual', display: this.translate.instant('companyProfile.membershipPlans.billingCycle.annual')},
      {value: 'monthly', display: this.translate.instant('companyProfile.membershipPlans.billingCycle.monthly')}
    ];
    this.initializeCurrency();
  }

  initializeCurrency() {
    if (this.company?.default_currency) {
      this.currency = this.company.default_currency;
    }
  }

  // Validation helpers
  get isNameInvalid(): boolean {
    return this.submitted && !this.name.trim();
  }

  get isCoverageInvalid(): boolean {
    return this.submitted && !this.coverage;
  }

  get isBillingCycleInvalid(): boolean {
    return this.submitted && !this.billing_cycle;
  }

  get isPriceInvalid(): boolean {
    return this.submitted && (this.price_cents === null || this.price_cents === undefined || this.price_cents < 0);
  }

  get isFormValid(): boolean {
    return !!(this.name.trim() && this.coverage && this.billing_cycle && this.price_cents >= 0);
  }

  addBenefit() {
    if (this.newBenefit.trim()) {
      this.benefits.push(this.newBenefit.trim());
      this.newBenefit = '';
    }
  }

  removeBenefit(index: number) {
    this.benefits.splice(index, 1);
  }

  onSave() {
    this.submitted = true;

    if (!this.isFormValid) {
      this.toastService.error(this.translate.instant('validation.fillRequiredFields'));
      return;
    }

    this.saving = true;
    const params: _AQH_MEM_5 = {
      name: this.name.trim(),
      description: this.description || undefined,
      coverage: this.coverage,
      billing_cycle: this.billing_cycle,
      price_cents: this.price_cents * 100, // Convert dollars to cents
      currency: this.currency,
      max_children: this.coverage === 'family' ? (this.max_children ?? undefined) : undefined,
      benefits: this.benefits.length > 0 ? this.benefits : undefined
    };

    this.membershipService.createMembershipPlan(params).subscribe({
      next: () => {
        this.saving = false;
        this.toastService.success(this.translate.instant('companyProfile.membershipPlans.addPlan.success'));
        this.modal.close(true);
      },
      error: (err) => {
        this.saving = false;
        this.toastService.error(err.error?.reason || this.translate.instant('companyProfile.membershipPlans.addPlan.error'));
      }
    });
  }

  close() {
    this.modal.close();
  }

  /**
   * Calculate net amount after platform + payment fees
   * Stripe: 2.4% + 2.00 NOK, Platform: 2%
   * Net = price × 0.956 - 2.00
   */
  calculateNetAmount(price: number): number {
    if (!price || price <= 0) return 0;
    const net = price * 0.956 - 2;
    return Math.max(0, Math.round(net * 100) / 100);
  }
}
